import { AlertTriangle, Loader2, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

export function Spinner({ classe = 'h-5 w-5' }: { classe?: string }) {
  return <Loader2 className={`${classe} animate-spin`} aria-hidden />;
}

export function Carregando({ texto = 'A carregar…' }: { texto?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-tinta-500 dark:text-tinta-400">
      <Spinner />
      <span>{texto}</span>
    </div>
  );
}

export function Erro({ mensagem, aoTentar }: { mensagem: string; aoTentar?: () => void }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm
                 text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200"
    >
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      <div className="flex-1">
        <p>{mensagem}</p>
        {aoTentar && (
          <button type="button" onClick={aoTentar} className="mt-2 font-medium underline">
            Tentar novamente
          </button>
        )}
      </div>
    </div>
  );
}

export function Aviso({ children }: { children: ReactNode }) {
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm
                 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div>{children}</div>
    </div>
  );
}

export function Vazio({ titulo, descricao, acao }: {
  titulo: string; descricao?: string; acao?: ReactNode;
}) {
  return (
    <div className="py-16 text-center">
      <p className="font-medium text-tinta-700 dark:text-papel-300">{titulo}</p>
      {descricao && <p className="mt-1 text-sm text-tinta-500 dark:text-tinta-400">{descricao}</p>}
      {acao && <div className="mt-4 flex justify-center">{acao}</div>}
    </div>
  );
}

export function Modal({ aberto, aoFechar, titulo, children, largura = 'max-w-2xl' }: {
  aberto: boolean;
  aoFechar: () => void;
  titulo: string;
  children: ReactNode;
  largura?: string;
}) {
  // Escape closes, and the body cannot scroll behind the dialog.
  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') aoFechar(); };
    document.addEventListener('keydown', aoTeclar);
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.body.style.overflow = overflowAnterior;
    };
  }, [aberto, aoFechar]);

  if (!aberto) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-tinta-900/50 p-4 sm:p-6">
      <button
        type="button"
        className="fixed inset-0 -z-10 cursor-default"
        aria-label="Fechar"
        onClick={aoFechar}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className={`cartao my-8 w-full ${largura}`}
      >
        <div className="flex items-center justify-between border-b border-papel-200 px-5 py-4 dark:border-tinta-700">
          <h2 className="text-lg font-semibold">{titulo}</h2>
          <button
            type="button"
            onClick={aoFechar}
            className="rounded-lg p-1.5 text-tinta-400 hover:bg-papel-200 hover:text-tinta-600
                       dark:hover:bg-tinta-700 dark:hover:text-papel-300"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

export function Confirmacao({ aberto, titulo, mensagem, textoConfirmar = 'Eliminar', aCarregar, aoConfirmar, aoCancelar }: {
  aberto: boolean;
  titulo: string;
  mensagem: string;
  textoConfirmar?: string;
  aCarregar?: boolean;
  aoConfirmar: () => void;
  aoCancelar: () => void;
}) {
  return (
    <Modal aberto={aberto} aoFechar={aoCancelar} titulo={titulo} largura="max-w-md">
      <p className="text-sm text-tinta-600 dark:text-papel-300">{mensagem}</p>
      <div className="mt-6 flex justify-end gap-2">
        <button type="button" className="btn-secundario" onClick={aoCancelar} disabled={aCarregar}>
          Cancelar
        </button>
        <button type="button" className="btn-perigo" onClick={aoConfirmar} disabled={aCarregar}>
          {aCarregar && <Spinner classe="h-4 w-4" />}
          {textoConfirmar}
        </button>
      </div>
    </Modal>
  );
}

/**
 * A dropdown you can add to.
 *
 * Every list on these screens — local, fornecedor — is a closed set that the
 * business keeps growing. Offering only what is already there means the day a
 * new venue or a new agency appears, the shift gets filed under nothing, and
 * the per-venue and per-supplier figures quietly stop adding up. So the list
 * carries its own "+ Novo" and the new entry is selected on the spot.
 *
 * The caller owns the request: this component knows the shape of the form, not
 * which endpoint it belongs to. Nothing is derived here — an empty extra field
 * is sent as empty, never as a zero.
 */
export interface ItemLista { id: string; nome: string }

type CampoExtra =
  | { tipo: 'numero'; rotulo: string; largura?: string }
  | { tipo: 'escolha'; rotulo: string; opcoes: { valor: string; texto: string }[] };

export function ListaComNovo<T extends ItemLista>({
  id, rotulo, opcoes, valor, textoVazio, aoEscolher,
  podeAdicionar, rotuloNovo, placeholderNovo, extra, aoCriar, aoCriado, etiqueta, ajuda,
}: {
  id: string;
  rotulo: string;
  opcoes: T[];
  valor: string;
  textoVazio: string;
  aoEscolher: (id: string, opcao: T | undefined) => void;
  podeAdicionar: boolean;
  rotuloNovo: string;
  placeholderNovo: string;
  extra?: CampoExtra;
  aoCriar: (nome: string, extra: string) => Promise<T>;
  aoCriado: (item: T) => void;
  /** Optional decoration for an option, e.g. "(interno)". */
  etiqueta?: (opcao: T) => string;
  ajuda?: ReactNode;
}) {
  const [aAdicionar, setAAdicionar] = useState(false);
  const [nome, setNome] = useState('');
  const [valorExtra, setValorExtra] = useState(
    extra?.tipo === 'escolha' ? extra.opcoes[0]?.valor ?? '' : '',
  );
  const [erro, setErro] = useState<string | null>(null);
  const [aGuardar, setAGuardar] = useState(false);

  function fechar() {
    setAAdicionar(false);
    setNome('');
    setValorExtra(extra?.tipo === 'escolha' ? extra.opcoes[0]?.valor ?? '' : '');
    setErro(null);
  }

  async function guardar() {
    const limpo = nome.trim();
    if (!limpo) { setErro(`Escreva o nome: ${rotulo.toLowerCase()}.`); return; }

    // The same name typed twice selects what is already there. This is exactly
    // how the duplicate venue spellings were born the first time round.
    const repetido = opcoes.find((o) => o.nome.toLowerCase() === limpo.toLowerCase());
    if (repetido) {
      aoEscolher(repetido.id, repetido);
      fechar();
      return;
    }

    setAGuardar(true);
    setErro(null);
    try {
      const criado = await aoCriar(limpo, valorExtra);
      aoCriado(criado);
      aoEscolher(criado.id, criado);
      fechar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível guardar.');
    } finally {
      setAGuardar(false);
    }
  }

  if (aAdicionar) {
    return (
      <div>
        <span className="rotulo">{rotuloNovo.replace(/^\+\s*/, '')}</span>
        <div className="flex gap-2">
          <input
            autoFocus
            aria-label={placeholderNovo}
            className="campo"
            placeholder={placeholderNovo}
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            onKeyDown={(e) => {
              // This sits inside a larger form; Enter must add the entry, not
              // submit the whole record.
              if (e.key === 'Enter') { e.preventDefault(); void guardar(); }
              if (e.key === 'Escape') { e.preventDefault(); fechar(); }
            }}
          />
          {extra?.tipo === 'numero' && (
            <input
              aria-label={extra.rotulo}
              type="number" min="0" step="1"
              className={`campo ${extra.largura ?? 'w-24'}`}
              placeholder={extra.rotulo}
              value={valorExtra}
              onChange={(e) => setValorExtra(e.target.value)}
            />
          )}
          {extra?.tipo === 'escolha' && (
            <select
              aria-label={extra.rotulo}
              className="campo w-36"
              value={valorExtra}
              onChange={(e) => setValorExtra(e.target.value)}
            >
              {extra.opcoes.map((o) => (
                <option key={o.valor} value={o.valor}>{o.texto}</option>
              ))}
            </select>
          )}
        </div>
        <div className="mt-1 flex items-center gap-3 text-xs">
          <button type="button" className="font-medium text-marca-600 hover:underline"
                  onClick={() => void guardar()} disabled={aGuardar}>
            {aGuardar ? 'A guardar…' : 'Guardar'}
          </button>
          <button type="button" className="text-tinta-500 hover:underline" onClick={fechar}>
            Cancelar
          </button>
        </div>
        {erro && <p className="mt-1 text-xs text-red-600">{erro}</p>}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label htmlFor={id} className="rotulo">{rotulo}</label>
        {podeAdicionar && (
          <button
            type="button"
            className="mb-1 text-xs font-medium text-marca-600 hover:underline"
            onClick={() => setAAdicionar(true)}
          >
            {rotuloNovo}
          </button>
        )}
      </div>
      <select
        id={id} className="campo" value={valor}
        onChange={(e) => aoEscolher(e.target.value, opcoes.find((o) => o.id === e.target.value))}
      >
        <option value="">{textoVazio}</option>
        {opcoes.map((o) => (
          <option key={o.id} value={o.id}>{o.nome}{etiqueta?.(o) ?? ''}</option>
        ))}
      </select>
      {ajuda && <p className="mt-1 text-xs text-tinta-500 dark:text-tinta-400">{ajuda}</p>}
    </div>
  );
}
